import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import {
  useAddNotificationMutation,
  useUpdateNotificationMutation,
  useGetNotificationByIdQuery,
  usePublishNotificationMutation,
  useAllowFeedbackMutation,
} from "../api/notificationApi";
import { showToast } from "../../../core/utils/alert";
import { NotificationPayloadType } from "../types/notificationRequest";
import { uploadNotificationFile } from "../api/fileUploadApi";
import { useCenter } from "../../peoples/centers/hooks/useCenter";
import { useStudent } from "../../peoples/students/hooks/useStudent";
import store from "../../../core/data/redux/store";
import { notificationApi } from "../api/notificationApi";
import { useNotifications } from "../hooks/useNotifications";
import MultiSelect, { Option } from "../../../core/common/MultiSelect";

const AddNotification = () => {
  const routes = all_routes;
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const isEditMode = !!id;

  // API hooks
  const [addNotification, { isLoading: isAdding }] =
    useAddNotificationMutation();
  const [updateNotification, { isLoading: isUpdating }] =
    useUpdateNotificationMutation();
  const {
    data: notificationData,
    isLoading: isLoadingNotification,
    isError: isNotificationError,
  } = useGetNotificationByIdQuery({ id: id || "" }, { skip: !isEditMode });

  // Get centers and students from APIs
  const { useCenterList } = useCenter({ skip: true });
  // Lazy loading states for students
  const [studentPage, setStudentPage] = useState(1);
  const [accumulatedStudents, setAccumulatedStudents] = useState<any[]>([]);
  const [hasMoreStudents, setHasMoreStudents] = useState(true);
  const [studentSearch, setStudentSearch] = useState("");
  const LAZY_PAGE_SIZE = 100;
  const lastProcessedPage = useRef(0);
  const pageRequestedRef = useRef(false);
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [hasClickedStudentDropdown, setHasClickedStudentDropdown] = useState(false);

  const { getStudentState } = useStudent({ 
    size: LAZY_PAGE_SIZE, 
    page: studentPage,
    search: studentSearch,
    status: "active",
    skip: !hasClickedStudentDropdown
  });

  const { data: centerResponse, isLoading: isLoadingCenters } = useCenterList();
  const centers = centerResponse?.data?.items || [];
  const { students, isLoading: isLoadingStudents, isFetching: isFetchingStudents } = getStudentState();

  useEffect(() => {
    if (!hasClickedStudentDropdown) return;
    
    if (students && students.length >= 0) {
      if (isFetchingStudents) return;
      if (studentPage === lastProcessedPage.current) return;

      setHasMoreStudents(students.length === LAZY_PAGE_SIZE);
      lastProcessedPage.current = studentPage;
      pageRequestedRef.current = false;
      
      setAccumulatedStudents(prev => {
        if (studentPage === 1) return students;
        const newUnique = students.filter(
          (s: any) => !prev.some((p: any) => p.id === s.id)
        );
        return [...prev, ...newUnique];
      });
    }
  }, [students, studentPage, isFetchingStudents]);

  const loadMoreStudents = () => {
    if (!hasMoreStudents) return;
    if (pageRequestedRef.current) return;
    pageRequestedRef.current = true;
    setStudentPage(prev => prev + 1);
  };

  const handleStudentSearch = (value: string) => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      setStudentSearch(value);
      setStudentPage(1);
      lastProcessedPage.current = 0;
      pageRequestedRef.current = false;
    }, 500);
  };

  // Transform centers and students for the Select component
  const centerOptions = centers.map((center) => ({
    value: center.id.toString(),
    label: center.name,
  }));

  const studentOptions = accumulatedStudents.map((student) => ({
    value: student.id.toString(),
    label: student.name || "",
  }));

  const isSubmitting = isAdding || isUpdating;

  const [formData, setFormData] = useState<
    NotificationPayloadType & { scheduledDate: string | null }
  >({
    title: "",
    content: "",
    // isPublished: false,
    scheduledDate: null,
    toAllCenters: false,
    toAllStudents: false,
    center_ids: [] as number[],
    student_ids: [] as number[],
    allowFeedback: false,
  });

  const [selectedFiles, setSelectedFiles] = useState<FileList | null>(null);
  const [existingAttachments, setExistingAttachments] = useState<string[]>([]);
  const [selectedCenters, setSelectedCenters] = useState<Option[]>([]);
  const [selectedStudents, setSelectedStudents] = useState<Option[]>([]);

  const [publishNotification] = usePublishNotificationMutation();
  const [setAllowFeedback] = useAllowFeedbackMutation();

  const { handleRemoveFiles } = useNotifications();

  const [isDeleting, setIsDeleting] = useState<{ [key: string]: boolean }>({});

  const hasInitializedForm = useRef(false);
  const hasInitializedCenters = useRef(false);
  const hasInitializedStudents = useRef(false);

  // Load notification data when in edit mode
  useEffect(() => {
    if (isEditMode && notificationData?.data?.notification && !hasInitializedForm.current) {
      const notification = notificationData.data.notification;

      // Format existing data to match form structure
      setFormData({
        title: notification.title || "",
        content: notification.content || "",
        isPublished: notification.isPublished || false,
        scheduledDate: notification.publishedOn || null,
        toAllCenters: notification.toAllCenters || false,
        toAllStudents: notification.toAllStudents || false,
        center_ids: notification.center_ids || [],
        student_ids: notification.student_ids || [],
        allowFeedback: notification.allowFeedback || false,
      });

      // Set existing attachments if any
      if (notification.attachments && notification.attachments.length > 0) {
        setExistingAttachments(
          notification.attachments.filter((att: string) => att !== "")
        );
      }
      
      hasInitializedForm.current = true;
    }
  }, [isEditMode, notificationData]);

  // Initialize center selections
  useEffect(() => {
    if (isEditMode && notificationData?.data?.notification && !hasInitializedCenters.current) {
      const notification = notificationData.data.notification;
      if (!notification.toAllCenters && notification.center_ids && notification.center_ids.length > 0) {
        if (notification.center_names && notification.center_names.length === notification.center_ids.length) {
          const selectedCenterOptions = notification.center_ids.map((id: number, index: number) => ({
            value: id.toString(),
            label: notification.center_names[index]
          }));
          setSelectedCenters(selectedCenterOptions);
          hasInitializedCenters.current = true;
        } else if (centers.length > 0) {
          const selectedCenterOptions = notification.center_ids
            .map((centerId: number) => {
              const center = centers.find((c: any) => c.id === centerId);
              return center ? { value: center.id.toString(), label: center.name } : null;
            })
            .filter(Boolean) as Option[];
          
          if (selectedCenterOptions.length > 0) {
            setSelectedCenters(selectedCenterOptions);
            hasInitializedCenters.current = true;
          }
        }
      } else {
        hasInitializedCenters.current = true;
      }
    }
  }, [isEditMode, notificationData, centers]);

  // Initialize student selections
  useEffect(() => {
    if (isEditMode && notificationData?.data?.notification && !hasInitializedStudents.current) {
      const notification = notificationData.data.notification;
      if (!notification.toAllStudents && notification.student_ids && notification.student_ids.length > 0) {
        if (notification.student_names && notification.student_names.length === notification.student_ids.length) {
          const selectedStudentOptions = notification.student_ids.map((id: number, index: number) => ({
            value: id.toString(),
            label: notification.student_names[index]
          }));
          setSelectedStudents(selectedStudentOptions);
          hasInitializedStudents.current = true;
        } else if (accumulatedStudents.length > 0) {
          const selectedStudentOptions = notification.student_ids
            .map((studentId: number) => {
              const student = accumulatedStudents.find((s: any) => s.id === studentId);
              return student
                ? { value: student.id.toString(), label: student.name || "" }
                : null;
            })
            .filter(Boolean) as Option[];
            
          if (selectedStudentOptions.length === notification.student_ids.length) {
            setSelectedStudents(selectedStudentOptions);
            hasInitializedStudents.current = true;
          } else if (selectedStudentOptions.length > 0 && !hasMoreStudents) {
            setSelectedStudents(selectedStudentOptions);
            hasInitializedStudents.current = true;
          }
        }
      } else {
        hasInitializedStudents.current = true;
      }
    }
  }, [isEditMode, notificationData, accumulatedStudents, hasMoreStudents]);

  // Helper function to get the appropriate icon and color based on file extension
  const getFileIcon = (
    fileName: string
  ): { iconClass: string; colorClass: string } => {
    const extension = fileName.split(".").pop()?.toLowerCase() || "";

    // Start with default values
    let iconClass = "file";
    let colorClass = "text-secondary";

    // Check file extension and set appropriate icon and color
    if (["pdf"].includes(extension)) {
      iconClass = "file-pdf";
      colorClass = "text-danger";
    } else if (
      ["jpg", "jpeg", "png", "gif", "webp", "svg"].includes(extension)
    ) {
      iconClass = "image"; // Changed from file-image to image
      colorClass = "text-success";
    } else if (["doc", "docx"].includes(extension)) {
      iconClass = "file-word";
      colorClass = "text-primary";
    } else if (["xls", "xlsx", "csv"].includes(extension)) {
      iconClass = "file-excel";
      colorClass = "text-success";
    } else if (["ppt", "pptx"].includes(extension)) {
      iconClass = "file-powerpoint";
      colorClass = "text-warning";
    } else if (["zip", "rar", "7z"].includes(extension)) {
      iconClass = "file-archive";
      colorClass = "text-info";
    } else if (["mp4", "avi", "mov", "webm"].includes(extension)) {
      iconClass = "file-video";
      colorClass = "text-purple";
    } else if (["mp3", "wav", "ogg"].includes(extension)) {
      iconClass = "file-audio";
      colorClass = "text-info";
    } else if (["txt", "log", "md"].includes(extension)) {
      iconClass = "file-alt";
      colorClass = "text-secondary";
    } else {
      iconClass = "file-circle-question";
      colorClass = "text-muted";
    }

    return { iconClass, colorClass };
  };

  // Helper function to format file size
  const formatFileSize = (size: number): string => {
    if (size < 1024) {
      return `${size} B`;
    } else if (size < 1024 * 1024) {
      return `${(size / 1024).toFixed(1)} KB`;
    } else if (size < 1024 * 1024 * 1024) {
      return `${(size / (1024 * 1024)).toFixed(1)} MB`;
    } else {
      return `${(size / (1024 * 1024 * 1024)).toFixed(1)} GB`;
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;

    if (type === "checkbox") {
      const target = e.target as HTMLInputElement;

      if (name === "toAllCenters") {
        setFormData({
          ...formData,
          toAllCenters: target.checked,
          center_ids: target.checked ? [] : formData.center_ids,
        });

        if (target.checked) {
          setSelectedCenters([]);
        }
      } else if (name === "toAllStudents") {
        setFormData({
          ...formData,
          toAllStudents: target.checked,
          student_ids: target.checked ? [] : formData.student_ids,
        });

        if (target.checked) {
          setSelectedStudents([]);
        }
      } else {
        setFormData({
          ...formData,
          [name]: target.checked,
        });
      }
    } else {
      setFormData({
        ...formData,
        [name]: value || "",
      });
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setSelectedFiles(e.target.files);
    }
  };

  const handleRemoveExistingAttachment = async (attachmentName: string) => {
    if (isEditMode && id) {
      // Show loading state
      setIsDeleting((prev) => ({ ...prev, [attachmentName]: true }));

      try {
        const success = await handleRemoveFiles(id, [attachmentName], () => {
          // Update local state only after successful API call
          setExistingAttachments((prev) =>
            prev.filter((name) => name !== attachmentName)
          );

          // Invalidate the cache to refresh the notification data
          store.dispatch(
            notificationApi.util.invalidateTags([{ type: "Notifications", id }])
          );
        });

        if (!success) {
          showToast({
            title: "Error",
            text: "Failed to remove file. Please try again.",
            icon: "error",
          });
        }
      } catch (error: any) {
        showToast({
          title: "Error",
          text: error.message || "Failed to remove file",
          icon: "error",
        });
        console.error("Error removing file:", error);
      } finally {
        // Reset loading state
        setIsDeleting((prev) => ({ ...prev, [attachmentName]: false }));
      }
    } else {
      // In create mode or if no ID, just remove from local state
      setExistingAttachments((prev) =>
        prev.filter((name) => name !== attachmentName)
      );
    }
  };

  const handleCenterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      toAllCenters: checked,
      center_ids: checked ? [] : prev.center_ids,
    }));
    if (checked) {
      setSelectedCenters([]);
    }
  };

  const handleStudentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      toAllStudents: checked,
      student_ids: checked ? [] : prev.student_ids,
    }));
    if (checked) {
      setSelectedStudents([]);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      // First, declare shouldPublish with a default boolean value
      const shouldPublish: boolean = formData.isPublished === true;

      // Step 1: Create/update the notification without the publish state
      // Remove isPublished and handle scheduledDate
      const {
        isPublished,
        scheduledDate,
        center_ids,
        student_ids,
        allowFeedback,
        ...payloadBase
      } = formData;

      // Create the payload with correctly named fields
      const payload = {
        ...payloadBase,
        // Use correct field names for centers and students
        center_ids: formData.toAllCenters ? [] : center_ids,
        student_ids: formData.toAllStudents ? [] : student_ids,
        // Only add publishedOn if scheduledDate is not null
        ...(scheduledDate ? { publishedOn: scheduledDate } : {}),
      };

      let result;
      if (isEditMode) {
        result = await updateNotification({
          id: id!,
          payload,
        }).unwrap();
      } else {
        result = await addNotification(payload).unwrap();
      }

      // Get notification ID from result
      const notificationId = isEditMode
        ? id!
        : result.data.notification.id.toString();
      console.log("Notification created/updated with ID:", notificationId);

      // Step 2: Upload attachments if any
      let uploadSuccessCount = 0;
      let uploadErrorCount = 0;

      if (selectedFiles && selectedFiles.length > 0) {
        console.log(
          `Uploading ${selectedFiles.length} files for notification ${notificationId}`
        );

        for (const file of Array.from(selectedFiles)) {
          // Determine file type based on extension
          const fileExtension = file.name.split(".").pop()?.toLowerCase() || "";
          let fileType = "document"; // Default to document

          if (
            ["jpg", "jpeg", "png", "gif", "webp", "svg"].includes(fileExtension)
          ) {
            fileType = "image";
          } else if (["mp4", "avi", "mov", "webm"].includes(fileExtension)) {
            fileType = "video";
          } else if (["mp3", "wav", "ogg"].includes(fileExtension)) {
            fileType = "audio";
          } else if (["zip", "rar", "7z"].includes(fileExtension)) {
            fileType = "other";
          }

          try {
            await uploadNotificationFile(notificationId, file, fileType);
            uploadSuccessCount++;
          } catch (error: any) {
            console.error(`Error uploading file ${file.name}:`, error);
            uploadErrorCount++;
          }
        }
      }

      // Step 3: Handle publish state based on checkbox
      let publishStateChanged = false;
      let finalPublishState = false;

      // Then update the comparison in the edit mode section
      if (isEditMode) {
        // Get current publish state from the loaded notification
        const currentPublishState: boolean =
          notificationData?.data?.notification?.isPublished === true;

        publishStateChanged = shouldPublish !== currentPublishState;
        finalPublishState = shouldPublish;

        // Check if the publish state has changed
        if (publishStateChanged) {
          try {
            console.log(
              `${
                shouldPublish ? "Publishing" : "Unpublishing"
              } notification ${notificationId}`
            );

            await publishNotification({
              id: notificationId,
              isPublished: shouldPublish,
            }).unwrap();

            console.log(
              `Notification ${
                shouldPublish ? "published" : "unpublished"
              } successfully`
            );
          } catch (publishError: any) {
            console.error(
              `Error ${
                shouldPublish ? "publishing" : "unpublishing"
              } notification:`,
              publishError
            );

            showToast({
              title: "Warning",
              text: `Notification was updated but could not be ${
                shouldPublish ? "published" : "unpublished"
              }. You can try again later.`,
              icon: "warning",
            });
          }
        }
      }
      // For new notifications, publish if checkbox is checked
      else if (shouldPublish) {
        publishStateChanged = true;
        finalPublishState = true;

        try {
          console.log(`Publishing new notification ${notificationId}`);

          await publishNotification({
            id: notificationId,
            isPublished: true,
          }).unwrap();

          console.log("New notification published successfully");
        } catch (publishError: any) {
          console.error("Error publishing new notification:", publishError);

          showToast({
            title: "Warning",
            text: "Notification was created but could not be published. You can publish it later.",
            icon: "warning",
          });
        }
      }


      // Step 4: Handle allowFeedback state
      try {
        console.log(
          `Setting allowFeedback to ${formData.allowFeedback} for notification ${notificationId}`
        );
        await setAllowFeedback({
          id: notificationId,
          allowFeedback: !!formData.allowFeedback,
        }).unwrap();
      } catch (feedbackError: any) {
        console.error("Error setting allowFeedback:", feedbackError);
        showToast({
          title: "Warning",
          text: "Notification saved but could not update feedback setting.",
          icon: "warning",
        });
      }

      // Display appropriate success message
      let successMessage = `Notification ${
        isEditMode ? "updated" : "created"
      } successfully!`;

      // Add publish state info to message
      if (publishStateChanged) {
        successMessage += ` Notification was ${
          finalPublishState ? "published" : "unpublished"
        }.`;
      }

      // Add attachment info to message
      if (uploadSuccessCount > 0) {
        successMessage += ` ${uploadSuccessCount} attachment(s) uploaded successfully.`;
      }
      if (uploadErrorCount > 0) {
        successMessage += ` ${uploadErrorCount} attachment(s) failed to upload.`;
      }

      showToast({
        title: "Success",
        text: successMessage,
        icon: "success",
      });

      // If files were uploaded in edit mode, navigate to detail view with a timestamp param
      // to force React Router to treat it as a new navigation and remount the component
      if (isEditMode && (uploadSuccessCount > 0 || uploadErrorCount > 0)) {
        // Explicitly invalidate the cache for this notification
        store.dispatch(
          notificationApi.util.invalidateTags([
            { type: "Notifications", id: notificationId },
          ])
        );

        // Navigate with timestamp to force a refresh
        navigate(
          `${routes.notificationDetail.replace(
            ":id",
            notificationId
          )}?t=${Date.now()}`
        );
      } else {
        navigate(routes.notificationList);
      }
    } catch (error: any) {
      showToast({
        title: "Error",
        text:
          error.message ||
          `Failed to ${isEditMode ? "update" : "create"} notification`,
        icon: "error",
      });
      console.error("Error:", error);
    }
  };

  if (isEditMode && isLoadingNotification) {
    return (
      <div className="page-wrapper">
        <div className="content content-two">
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-2">Loading notification data...</p>
          </div>
        </div>
      </div>
    );
  }

  if (isEditMode && isNotificationError) {
    return (
      <div className="page-wrapper">
        <div className="content content-two">
          <div className="alert alert-danger m-3">
            Error loading notification data. Please try again.
            <div className="mt-3">
              <Link to={routes.notificationList} className="btn btn-primary">
                Back to Notifications List
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Page Wrapper */}
      <div className="page-wrapper">
        <div className="content content-two">
          {/* Page Header */}
          <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
            <div className="my-auto mb-2">
              <h3 className="mb-1">
                {isEditMode ? "Edit" : "Add"} Notification
              </h3>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={routes.adminDashboard}>Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <Link to={routes.notificationList}>Notifications</Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    {isEditMode ? "Edit" : "Add"} Notification
                  </li>
                </ol>
              </nav>
            </div>
          </div>
          {/* /Page Header */}
          <div className="row">
            <div className="col-md-12">
              <form onSubmit={handleSubmit}>
                {/* Notification Information Section */}
                <div className="card mb-3">
                  <div className="card-header bg-light">
                    <div className="d-flex align-items-center">
                      <span className="bg-white avatar avatar-sm me-2 text-gray-7 flex-shrink-0">
                        <i className="ti ti-info-square-rounded fs-16" />
                      </span>
                      <h4 className="text-dark">Notification Information</h4>
                    </div>
                  </div>
                  <div className="card-body pb-1">
                    <div className="row">
                      <div className="col-md-12">
                        <div className="mb-3">
                          <label className="form-label">
                            Title <span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            name="title"
                            placeholder="Enter notification title"
                            value={formData.title || ""}
                            onChange={handleInputChange}
                            required
                          />
                        </div>
                      </div>
                      {/* <div className="col-md-6">
                        <div className="mb-3">
                          <label className="form-label">Schedule Date</label>
                          <div className="input-icon position-relative">
                            <DatePicker
                              className="form-control datetimepicker"
                              format={{
                                format: "DD-MM-YYYY",
                                type: "mask",
                              }}
                              value={
                                formData.scheduledDate
                                  ? dayjs(formData.scheduledDate)
                                  : undefined
                              }
                              onChange={handleDateChange}
                              placeholder="Select Date"
                            />
                            <span className="input-icon-addon">
                              <i className="ti ti-calendar" />
                            </span>
                          </div>
                        </div>
                      </div> */}
                      <div className="col-12">
                        <div className="mb-3">
                          <label className="form-label">
                            Content <span className="text-danger">*</span>
                          </label>
                          <textarea
                            className="form-control"
                            name="content"
                            rows={6}
                            placeholder="Enter notification content"
                            value={formData.content || ""}
                            onChange={handleInputChange}
                            required
                          ></textarea>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="form-label">Attachments</label>
                          <div className="profile-upload">
                            {/* Existing attachments */}
                            {existingAttachments.length > 0 && (
                              <div className="existing-files mb-3">
                                <h6 className="mb-2">Current Attachments</h6>
                                {existingAttachments.map(
                                  (attachment, index) => {
                                    const { iconClass, colorClass } =
                                      getFileIcon(attachment);
                                    return (
                                      <div
                                        key={`existing-${index}`}
                                        className="d-flex align-items-center mb-1"
                                      >
                                        <div className="attachment-icon me-2">
                                          <i
                                            className={`fas fa-${iconClass} ${colorClass}`}
                                          ></i>
                                        </div>
                                        <span>{attachment}</span>
                                        <button
                                          type="button"
                                          className="btn btn-sm text-danger ms-auto"
                                          onClick={() =>
                                            handleRemoveExistingAttachment(
                                              attachment
                                            )
                                          }
                                        >
                                          <i className="ti ti-x"></i>
                                        </button>
                                      </div>
                                    );
                                  }
                                )}
                                <hr />
                              </div>
                            )}

                            {/* New attachments upload */}
                            <div className="profile-uploader d-flex align-items-center">
                              <div className="drag-upload-btn mb-3">
                                Choose File
                                <input
                                  type="file"
                                  className="form-control image-sign"
                                  onChange={handleFileChange}
                                  multiple
                                  accept="*/*"
                                />
                              </div>
                              {selectedFiles && selectedFiles.length > 0 && (
                                <span className="ms-2 text-muted">
                                  {selectedFiles.length} file(s) selected
                                </span>
                              )}
                            </div>
                            <p className="fs-12">
                              Upload multiple files (max size: 5MB per file)
                            </p>
                            {selectedFiles && selectedFiles.length > 0 && (
                              <div className="selected-files mt-2">
                                <h6 className="mb-2">New Attachments</h6>
                                {Array.from(selectedFiles).map(
                                  (file, index) => {
                                    const { iconClass, colorClass } =
                                      getFileIcon(file.name);
                                    return (
                                      <div
                                        key={index}
                                        className="d-flex align-items-center mb-1"
                                      >
                                        <div className="attachment-icon me-2">
                                          <i
                                            className={`fas fa-${iconClass} ${colorClass}`}
                                          ></i>
                                        </div>
                                        <span>{file.name}</span>
                                        <span className="ms-2 text-muted">
                                          ({formatFileSize(file.size)})
                                        </span>
                                      </div>
                                    );
                                  }
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="mb-3">
                          <div className="form-check mt-0 mt-md-4">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              id="allowFeedbackCheck"
                              name="allowFeedback"
                              checked={!!formData.allowFeedback}
                              onChange={handleInputChange}
                            />
                            <label
                              className="form-check-label"
                              htmlFor="allowFeedbackCheck"
                            >
                              Allow Feedback
                            </label>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="mb-3">
                          <div className="form-check mt-0 mt-md-4">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              id="publishCheck"
                              name="isPublished"
                              checked={!!formData.isPublished}
                              onChange={handleInputChange}
                            />
                            <label
                              className="form-check-label"
                              htmlFor="publishCheck"
                            >
                              {isEditMode
                                ? formData.isPublished
                                  ? "Keep Published"
                                  : "Publish After Update"
                                : "Publish Immediately"}
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Recipients Section */}
                <div className="card mb-3">
                  <div className="card-header bg-light">
                    <div className="d-flex align-items-center">
                      <span className="bg-white avatar avatar-sm me-2 text-gray-7 flex-shrink-0">
                        <i className="ti ti-users fs-16" />
                      </span>
                      <h4 className="text-dark">Notification Recipients</h4>
                    </div>
                  </div>
                  <div className="card-body pb-1">
                    <div className="row">
                      {/* Centers Section */}
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="form-label">Centers</label>
                          <div className="form-check mb-2">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              id="allCentersCheck"
                              name="toAllCenters"
                              checked={formData.toAllCenters}
                              onChange={handleCenterChange}
                            />
                            <label
                              className="form-check-label"
                              htmlFor="allCentersCheck"
                            >
                              Send to All Centers
                            </label>
                          </div>

                          {!formData.toAllCenters && (
                            <div>
                              <MultiSelect
                                className="select"
                                options={centerOptions}
                                isMulti={true}
                                value={selectedCenters}
                                onChange={(selected) => {
                                  if (selected) {
                                    setSelectedCenters(selected);
                                    setFormData((prev) => ({
                                      ...prev,
                                      toAllCenters: false,
                                      center_ids: selected.map((center) =>
                                        parseInt(center.value)
                                      ),
                                    }));
                                  } else {
                                    setSelectedCenters([]);
                                    setFormData((prev) => ({
                                      ...prev,
                                      center_ids: [],
                                    }));
                                  }
                                }}
                                placeholder={
                                  isLoadingCenters
                                    ? "Loading centers..."
                                    : "Select centers..."
                                }
                              />
                              {selectedCenters.length === 0 && (
                                <small className="text-muted d-block mt-1">
                                  No centers selected
                                </small>
                              )}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Students Section */}
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="form-label">Students</label>
                          <div className="form-check mb-2">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              id="allStudentsCheck"
                              name="toAllStudents"
                              checked={formData.toAllStudents}
                              onChange={handleStudentChange}
                            />
                            <label
                              className="form-check-label"
                              htmlFor="allStudentsCheck"
                            >
                              Send to All Students
                            </label>
                          </div>

                          {!formData.toAllStudents && (
                            <div>
                              <MultiSelect
                                className="select"
                                options={studentOptions}
                                isMulti={true}
                                value={selectedStudents}
                                onChange={(selected) => {
                                  if (selected) {
                                    setSelectedStudents(selected);
                                    setFormData((prev) => ({
                                      ...prev,
                                      toAllStudents: false,
                                      student_ids: selected.map((student) =>
                                        parseInt(student.value)
                                      ),
                                    }));
                                  } else {
                                    setSelectedStudents([]);
                                    setFormData((prev) => ({
                                      ...prev,
                                      student_ids: [],
                                    }));
                                  }
                                }}
                                placeholder={
                                  isLoadingStudents && studentPage === 1
                                    ? "Loading students..."
                                    : "Select students..."
                                }
                                onMenuScrollToBottom={loadMoreStudents}
                                isLoading={isFetchingStudents}
                                onInputChange={handleStudentSearch}
                                onMenuOpen={() => setHasClickedStudentDropdown(true)}
                              />
                              {selectedStudents.length === 0 && (
                                <small className="text-muted d-block mt-1">
                                  No students selected
                                </small>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Form Actions */}
                <div className="text-end">
                  <Link
                    to={routes.notificationList}
                    className="btn btn-light me-3"
                  >
                    Cancel
                  </Link>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-1"
                          role="status"
                        ></span>
                        {isEditMode ? "Updating..." : "Saving..."}
                      </>
                    ) : isEditMode ? (
                      "Update Notification"
                    ) : (
                      "Save Notification"
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
      {/* /Page Wrapper */}
    </>
  );
};

export default AddNotification;
